import { ShoppingCart, Star, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trackAddToCart } from '@/lib/analytics';
import type { Product } from '@/data/allProducts';
import { getProductPath, getOptimizedImageUrl, getLocalizedTitle } from '@/data/allProducts';

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  index: number;
}

const TAG_STYLES: Record<string, string> = {
  'Hot':                'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Trending':           'bg-chart-2/20 text-chart-2 border-chart-2/30',
  'Best Seller':        'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Premium':            'bg-primary/20 text-primary border-primary/30',
  'Special Discount':   'bg-red-500/20 text-red-400 border-red-500/30',
  'Limited Time Offer': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'New Arrival':        'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Low Stock':          'bg-destructive/20 text-destructive border-destructive/30',
};

const getTagStyle = (tag: string) =>
  TAG_STYLES[tag] ?? 'bg-secondary/60 text-foreground border-border';

const ProductCard = ({ product, onAddToCart, index }: ProductCardProps) => {
  const { t, i18n } = useTranslation();
  // Support both old single `badge` and new `tags` array
  const tags: string[] = product.tags?.length
    ? product.tags
    : product.badge
    ? [product.badge]
    : [];

  const isOutOfStock = product.inventoryCount !== undefined && product.inventoryCount === 0;
  const isLowStock = product.inventoryCount !== undefined && product.inventoryCount > 0 && product.inventoryCount <= 5;
  const displayTitle = getLocalizedTitle(product, i18n.language);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      className="group glass rounded-xl overflow-hidden hover:neon-glow transition-all duration-500"
    >
      <Link to={getProductPath(product)}>
        <div className="relative aspect-square bg-secondary/30 flex items-center justify-center overflow-hidden">
          {product.image ? (
            <div className="w-full h-full relative group-hover:scale-105 transition-transform duration-1000 ease-out">
              <img
                src={getOptimizedImageUrl(product.image, 480)}
                onError={(e) => { if (e.currentTarget.src !== product.image) e.currentTarget.src = product.image; }}
                alt={displayTitle}
                className={`w-full h-full object-cover transition-opacity duration-700 ease-in-out ${isOutOfStock ? 'opacity-50 grayscale' : ''} ${product.images && product.images.length > 1 ? 'group-hover:opacity-0' : ''}`}
                loading="lazy"
              />
              {product.images && product.images.length > 1 && !isOutOfStock && (
                <img
                  src={getOptimizedImageUrl(product.images[1], 480)}
                  onError={(e) => { if (e.currentTarget.src !== product.images[1]) e.currentTarget.src = product.images[1]; }}
                  alt={`${displayTitle} alternate view`}
                  className="w-full h-full object-cover absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"
                  loading="lazy"
                />
              )}
            </div>
          ) : (
            <div className={`text-4xl font-display font-bold text-muted-foreground/20 group-hover:scale-110 transition-transform duration-500 ${isOutOfStock ? 'opacity-40' : ''}`}>
              {product.category[0]}
            </div>
          )}

          {/* Out of Stock overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-[1px]">
              <span className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest bg-background/80 text-muted-foreground border border-border">
                Out of Stock
              </span>
            </div>
          )}

          {/* Low stock banner */}
          {isLowStock && (
            <div className="absolute bottom-0 left-0 right-0 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider bg-orange-500/80 text-white">
              Only {product.inventoryCount} left!
            </div>
          )}

          {/* Tag badges — stacked top-right, up to 2 visible */}
          {tags.length > 0 && !isOutOfStock && (
            <div className="absolute top-3 end-3 flex flex-col gap-1 items-end">
              {tags.slice(0, 2).map(tag => (
                <span
                  key={tag}
                  className={`px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full border ${getTagStyle(tag)}`}
                >
                  {tag}
                </span>
              ))}
              {tags.length > 2 && (
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-secondary/80 text-muted-foreground border border-border">
                  +{tags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </Link>

      <div className="p-3 sm:p-4">
        <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">{product.category}</p>
        <Link to={getProductPath(product)}>
          <h3 className="text-sm font-medium text-foreground leading-snug mb-1 line-clamp-2 hover:text-primary transition-colors">
            {displayTitle}
          </h3>
        </Link>
        <p className="text-[11px] text-muted-foreground leading-snug mb-1.5 line-clamp-2 min-h-[2.5em]">
          {product.shortDescription || '—'}
        </p>
        <div className="flex items-center gap-1 mb-2">
          <Star className="h-3 w-3 fill-chart-3 text-chart-3" />
          <span className="text-xs text-muted-foreground">{product.rating} ({product.reviews})</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold text-foreground">
                {t('products.egp')} {
                  product.defaultVariant && product.variants?.find(v => v.label === product.defaultVariant)
                    ? product.variants.find(v => v.label === product.defaultVariant)?.price
                    : product.price
                }
              </span>
              {(product.compareAtPrice || (product.defaultVariant && product.variants?.find(v => v.label === product.defaultVariant)?.price < product.price)) && (
                <span className="text-xs text-muted-foreground line-through">
                  {t('products.egp')} {product.compareAtPrice || product.price}
                </span>
              )}
              {product.promoLabel && (!product.promoExpires || new Date(product.promoExpires) > new Date()) && (
                <span className="text-[10px] font-bold text-destructive uppercase tracking-wide">
                  {product.promoLabel}
                </span>
              )}
            </div>
            {product.defaultVariant && (
              <span className="text-[10px] text-primary font-medium mt-0.5">
                Default: {product.defaultVariant}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {!isOutOfStock && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); trackAddToCart({ id: product.id, title: product.title, price: product.price, category: product.category }); onAddToCart(product); }}
                className="p-3 sm:p-2.5 rounded-full bg-primary text-primary-foreground shadow-md transition-all duration-200 active:scale-95 hover:bg-primary/90 touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Add to cart"
              >
                <ShoppingCart className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                const url = `${window.location.origin}${getProductPath(product)}`;
                if (navigator.share) {
                  try { await navigator.share({ title: displayTitle, url }); } catch {}
                } else {
                  await navigator.clipboard.writeText(url);
                }
              }}
              className="p-3 sm:p-2.5 rounded-full bg-secondary/80 text-muted-foreground shadow-md transition-all duration-200 active:scale-95 hover:text-foreground touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Share product"
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;








